import {
  RESCHEDULING_GHOST_HATCH,
  RESCHEDULING_GHOST_OUTLINE,
} from '../../../shared/lib/schedule';
import type { WeekChip } from '../model/weekTypes';

interface Props {
  chip: WeekChip;
  /** Take the whole cell (Krankheit / Urlaub) rather than one line of it. */
  fill?: boolean;
}

/**
 * One entry in a week cell.
 *
 * **Filled exactly like a `SegmentCell` on the day board** — the block's own colour edge to edge,
 * white text, same type scale, same pills. A Termin has to look like the same object in both views;
 * an outlined card here and a solid block there read as two different kinds of thing.
 *
 * What it does not reproduce is the Anfahrt / Arbeit / Abfahrt split: with no time axis there is no
 * width to divide, so the chip takes the work colour. `chip.time` is the **Arbeit** window to match
 * — the travel minutes are a day-board concern and appear only on its Anfahrt strip.
 *
 * Not clickable — the click belongs to the cell, which goes to the day board.
 */
export const WeekEventChip = ({ chip, fill = false }: Props) => {
  // A Pause happens on nearly every working day. Rendered at full weight it would be the loudest
  // thing in the grid while being the least interesting, so it keeps the fill but drops to one line.
  const isBreak = chip.kind === 'BREAK';

  return (
    <div
      title={chip.tooltip}
      className={`relative isolate flex flex-col overflow-hidden rounded-sm px-1.5 py-1 font-medium leading-tight text-white ${
        fill ? 'flex-1 items-center justify-center text-center' : ''
      }`}
      style={{
        backgroundColor: chip.color,
        ...(chip.needsRescheduling ? { outline: RESCHEDULING_GHOST_OUTLINE, outlineOffset: '-2px' } : {}),
      }}
    >
      {(chip.needsRescheduling || chip.inProgress || (chip.time && !fill)) && (
        // `flex-wrap`: the time is a range, and both pills are `shrink-0`. Without it a chip
        // carrying `Umdisponieren` and `Läuft` in a 180px column truncates `09:00–11:00` away
        // entirely — the one thing this row exists to show. It drops to its own line instead.
        <div className="flex flex-wrap items-center gap-1 overflow-hidden">
          {chip.needsRescheduling && (
            // `z-10` lifts it above the hatch — the pill is the one part of a ghosted block that
            // has to stay crisp. Same treatment as on the day board.
            <span className="relative z-10 inline-flex shrink-0 items-center gap-1 rounded bg-amber-500 px-1 py-px text-[8px] font-bold uppercase leading-none text-white">
              <span className="h-1 w-1 shrink-0 rounded-full bg-white" />
              <span>Umdisponieren</span>
            </span>
          )}
          {chip.inProgress && (
            <span className="relative z-10 inline-flex shrink-0 items-center gap-1 rounded bg-red-500 px-1 py-px text-[8px] font-bold uppercase leading-none text-white">
              <span className="h-1 w-1 shrink-0 animate-pulse rounded-full bg-white" />
              <span>Läuft</span>
            </span>
          )}
          {chip.time && !fill && <span className="truncate text-[11px] font-semibold">{chip.time}</span>}
        </div>
      )}

      {/*
        Wrapped to two lines rather than truncated like `SegmentCell`: a day-board strip is as wide
        as its duration and has no room, a week column is 180px+ and an address is the whole point
        of the cell. A third line would push every row taller for one long street name.
      */}
      <span className={`line-clamp-2 ${isBreak ? 'text-[11px]' : fill ? 'text-sm font-semibold' : 'text-xs'}`}>{chip.label}</span>

      {chip.subLabel && (
        <span className="truncate text-[10px] opacity-80">{chip.subLabel}</span>
      )}

      {chip.needsRescheduling && (
        <div
          className="pointer-events-none absolute inset-0"
          style={{ backgroundImage: RESCHEDULING_GHOST_HATCH }}
          aria-hidden
        />
      )}

      {chip.inProgress && (
        <div
          className="pointer-events-none absolute inset-0 animate-pulse rounded-sm ring-2 ring-inset ring-white"
          aria-hidden
        />
      )}
    </div>
  );
};
